import { InputMask } from "@react-input/mask";
import Header from "@/atomic/atoms/Header";
import { PropsView } from "../Models/vehicle";
import Button from "@/atomic/atoms/Button";
import Loading from "@/atomic/atoms/Loading";
import Modal from "@/atomic/atoms/Modal";
import { Suspense } from "react";
import { Controller } from "react-hook-form";

const selectBase =
  "w-full h-14 px-4 pr-10 rounded-xl border font-primary text-base font-semibold text-text bg-background appearance-none cursor-pointer " +
  "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

const inputBase =
  "w-full h-14 px-4 rounded-xl border font-primary text-base font-semibold text-text bg-background uppercase " +
  "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary";

function fieldClass(hasError?: boolean) {
  return [
    selectBase,
    hasError ? "border-error" : "border-border",
  ].join(" ");
}

function inputFieldClass(hasError?: boolean) {
  return [
    inputBase,
    hasError ? "border-error" : "border-border",
  ].join(" ");
}

export default function RegisterVehicleView({
  goBack,
  onChangeSelect,
  goToLogin,
  isDisabled,
  types,
  brands,
  models,
  isLoading,
  error,
  errorPlate,
  errorCustomer,
  errorCreateUser,
  handlePlate,
  retryCreateUser,
  register,
  handleSubmit,
  errors,
  isVisibleModal,
  toggleModal,
  control,
}: PropsView) {
  return (
    <div className="h-full relative flex flex-col bg-white overflow-hidden">
      <Header
        title="Dados do carro"
        hasLeftButton
        goBack={goBack}
      />
      <main className="flex-1 w-full flex justify-center min-h-0 bg-background">
        <form
          className="w-full max-w-[480px] px-6 pt-6 pb-8 flex flex-col flex-1 min-h-0"
          onSubmit={handleSubmit}
        >
          <div className="flex-1 min-h-0 overflow-y-auto pb-4">
            <h2 className="text-text text-[22px] leading-tight font-primary font-semibold tracking-tight">
              Adicione os dados do carro
            </h2>

            <div className="mt-6 flex flex-col gap-5">
              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Placa<span className="text-error">*</span>
                </label>
                <InputMask
                  {...register("plate")}
                  name="plate"
                  placeholder="Placa"
                  className={inputFieldClass(!!errors.plate)}
                  mask="___-.@.."
                  replacement={{ _: /\D/, ".": /\d/, "@": /[A-Za-z0-9]*/ }}
                  type="text"
                />
                {errors.plate && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.plate.message as string}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Tipo
                </label>
                <div className="relative">
                  <Controller
                    control={control}
                    name="type"
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <select
                        onChange={e => onChangeSelect({
                          e,
                          onChange,
                        })}
                        value={value}
                        title="Tipo"
                        name="type"
                        className={fieldClass(!!errors.type)}
                      >
                        <option value="0">Tipo</option>
                        {types?.map((item, index) => (
                          <option key={index} value={item.id}>{item.text}</option>
                        ))}
                      </select>
                    )}
                  />
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {errors.type && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.type.message as string}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Marca
                </label>
                <div className="relative">
                  <Controller
                    control={control}
                    name="brand"
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <select
                        onChange={e => onChangeSelect({
                          e,
                          onChange,
                        })}
                        value={value}
                        title="Marca"
                        name="brand"
                        className={fieldClass(!!errors.brand)}
                      >
                        <option value="0">Marca</option>
                        {brands?.map((item, index) => (
                          <option key={index} value={item.value}>{item.text}</option>
                        ))}
                      </select>
                    )}
                  />
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {errors.brand && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.brand.message as string}
                  </span>
                )}
              </div>

              <div>
                <label className="block text-sm font-primary text-text mb-2">
                  Modelo
                </label>
                <div className="relative">
                  <Controller
                    control={control}
                    name="model"
                    rules={{
                      required: true,
                    }}
                    render={({ field: { onChange, value } }) => (
                      <select
                        onChange={e => onChangeSelect({
                          e,
                          onChange,
                        })}
                        value={value}
                        title="Modelo"
                        name="model"
                        className={fieldClass(!!errors.model)}
                      >
                        <option value="0">Modelo</option>
                        {models?.map((item, index) => (
                          <option key={index} value={item.value}>{item.text}</option>
                        ))}
                      </select>
                    )}
                  />
                  <svg
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-text pointer-events-none"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </div>
                {errors.model && (
                  <span className="mt-1 block text-error text-xs font-primary">
                    {errors.model.message as string}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-8 shrink-0">
            <Button
              className="rounded-theme-large bg-primary text-white font-primary font-semibold border-0"
              title="Continuar"
              type="submit"
              isDisabled={isDisabled}
            />
          </div>
        </form>
      </main>

      {error && isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title="Ops!"
            message="Houve um problema ao validar sua placa, quer tentar novamente?"
            actionMessage="Tentar novamente"
            action={() => {
              toggleModal();
              handlePlate();
            }}
            actionSecondMessage="Não"
            actionSecond={toggleModal}
          />
        </Suspense>
      )}
      {errorPlate && isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title="Atenção!"
            message="Esse veículo já possui um meio de pagamento automático cadastrado, não é possível adicionar ao Pedágio Eletrônico Sem Parar."
            actionMessage="Ok"
            action={toggleModal}
          />
        </Suspense>
      )}
      {errorCustomer && isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title="Atenção!"
            message="Você já é nosso cliente."
            actionMessage="Login"
            action={() => {
              toggleModal();
              goToLogin();
            }}
          />
        </Suspense>
      )}
      {errorCreateUser && isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title="Ops!"
            message="Houve um problema, quer tentar novamente?"
            actionMessage="Tentar novamente"
            action={retryCreateUser}
            actionSecondMessage="Não"
            actionSecond={toggleModal}
          />
        </Suspense>
      )}
      {isLoading && (
        <Suspense fallback={null}>
          <Loading />
        </Suspense>
      )}
    </div>
  );
}
